import { useForm } from 'react-final-form';
import { useSelector } from 'react-redux';
import { ReduxState } from 'ducks';
import { GuestType } from 'models/product';
import { isRequiredGuestType } from 'lib/util/util';
import { NumberInputGuestTypeListItem } from './NumberInputGuestTypeListItem';

import { getEnablePerBookingMaximumParticipantsValue } from 'lib/util/getEnablePerBookingMaximumParticipants';

interface Props {
  className?: string;
  listItemClassName?: string;
  guestTypes: GuestType[];
}

export const GuestTypeList = ({ className, listItemClassName, guestTypes }: Props) => {
  const form = useForm();
  const values = form.getState().values;

  const maximumParticipantRules = useSelector(
    (state: ReduxState) =>
      state.server.product?.product?.booking_widget_settings?.maximum_participant_rules ?? []
  );
  const minimumParticipantRules = useSelector(
    (state: ReduxState) =>
      state.server.product?.product?.booking_widget_settings?.minimum_participant_rules ?? []
  );
  const selectableParticipantRules = useSelector(
    (state: ReduxState) =>
      state.server.product?.product?.booking_widget_settings?.selectable_participant_rules ?? []
  );
  const isSelectableParticipantRuleActive = useSelector(
    (state: ReduxState) =>
      state.server.product?.product?.booking_widget_settings
        ?.is_selectable_participant_rule_active ?? false
  );
  const isPerBookingParticipantRuleActive = useSelector(
    (state: ReduxState) =>
      state.server.product?.product?.booking_widget_settings
        ?.is_per_booking_participant_rule_active ?? false
  );
  const perBookingMaximumParticipants = useSelector(
    (state: ReduxState) =>
      state.server.product?.product?.booking_widget_settings?.per_booking_participant_rule
        ?.maximum_participants ?? 0
  );

  const getMaximumParticipants = (unit: string, maximumParticipants: number): number => {
    // Hack: the value of values.guestCount is mixed by string and integer
    const parsedGuestCount: { [key: string]: number } = {};
    let totalGuestCount = 0;
    for (const key in values.guestCount) {
      parsedGuestCount[key] = Number(values.guestCount[key]);
      totalGuestCount += parsedGuestCount[key];
    }

    const remainingCapacity = perBookingMaximumParticipants - totalGuestCount;
    const enablePerBookingMaximumParticipantsValue = getEnablePerBookingMaximumParticipantsValue(
      isPerBookingParticipantRuleActive,
      perBookingMaximumParticipants,
      unit,
      maximumParticipants,
      remainingCapacity,
      values.guestCount
    );
    if (!isSelectableParticipantRuleActive) {
      return enablePerBookingMaximumParticipantsValue;
    }

    let newMaximumParticipants = enablePerBookingMaximumParticipantsValue;

    for (let i = 0; i < selectableParticipantRules.length; i++) {
      const rule = selectableParticipantRules[i];
      if (rule.restricted_units.includes(unit)) {
        newMaximumParticipants = 0;
        break;
      }
    }

    for (let i = 0; i < selectableParticipantRules.length; i++) {
      const rule = selectableParticipantRules[i];

      if (rule.required_units.includes(unit) || !rule.restricted_units.includes(unit)) {
        continue;
      }

      const numOfRequiredUnit = rule.required_units.reduce(
        (count: number, requiredUnit: string) => {
          return count + (parsedGuestCount[requiredUnit] || 0);
        },
        0
      );

      const numOfRestrictedUnit = rule.restricted_units.reduce(
        (count: number, restrictedUnit: string) => {
          return count + (parsedGuestCount[restrictedUnit] || 0);
        },
        0
      );

      const availableCapacity = numOfRequiredUnit * rule.maximum_participants - numOfRestrictedUnit;

      const tempMaximumParticipants = (parsedGuestCount[unit] || 0) + availableCapacity;

      if (newMaximumParticipants < tempMaximumParticipants) {
        newMaximumParticipants = tempMaximumParticipants;
        if (isPerBookingParticipantRuleActive) {
          if (enablePerBookingMaximumParticipantsValue < tempMaximumParticipants) {
            newMaximumParticipants = enablePerBookingMaximumParticipantsValue;
          }
        }
      }
    }
    return newMaximumParticipants;
  };

  return (
    <ul className={className}>
      {guestTypes.map((guestType: GuestType) => {
        const minPaxRule = minimumParticipantRules?.find((rule) => rule.unit === guestType.key);
        const maxPaxRule = maximumParticipantRules?.find((rule) => rule.unit === guestType.key);
        const maximumParticipants = getMaximumParticipants(
          guestType.key,
          maxPaxRule ? maxPaxRule.maximum_participants ?? 11 : 11
        );

        const minimumParticipants = minPaxRule
          ? minPaxRule.minimum_participants ?? 0
          : isRequiredGuestType(guestType)
          ? 1
          : 0;

        if (Number(values.guestCount?.[guestType.key] ?? 0) > maximumParticipants) {
          form.change(`guestCount.${guestType.key}`, 0);
        }

        return (
          <NumberInputGuestTypeListItem
            key={guestType.key}
            className={listItemClassName}
            guestType={guestType}
            minimumParticipants={minimumParticipants}
            maximumParticipants={maximumParticipants}
          />
        );
      })}
    </ul>
  );
};
